import { isSendableSmsLineType } from '@provision/conversation-types';
import { useEffect, useMemo } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  PartnerGroupRules,
  PartnerSetting,
  usePartnerGroupRules,
  usePartnerSettings,
  UserGroupRules,
  useUserGroupRuleValues,
} from 'src/graphql';
import { useChatCommonData } from 'src/pages/Conversations/Chat/registry/common-data-context';
import type { SmsChannelInfo } from 'src/pages/Conversations/Chat/SmsSession/sms-recipients';
import { isStringBooleanTrue } from 'src/utils/strings';
import { useOpenSmsSession } from './use-open-sms-session';
import { useSmsDeliverabilityCheck } from './use-sms-deliverability-check';

export interface SmsSwitchVisibility {
  smsGatesPass: boolean;
  showSmsSwitch: boolean;
  hasOpenSession: boolean;
  openSessionId: string | null;
  sessionOpenedAt: string | null;
  /**
   * User-visible reason when the client's number is known non-sendable
   * (e.g. landline). Null while unchecked (optimistic) or when sendable.
   */
  smsDisabledReason: string | null;
}

/**
 * The switch shows whenever the gates pass — no TwilioSMS channel required, the
 * backend creates one at startSmsSession time. Deliverability only disables it:
 * a default channel with a cached non-sendable line type renders the SMS tab inert.
 */
export function useSmsSwitchVisibility(
  conversationId: string,
  defaultChannel: SmsChannelInfo | undefined,
): SmsSwitchVisibility {
  const conversationPartnerId = useChatCommonData((s) => s.partnerId);

  const partnerId = Number(conversationPartnerId);
  const { partnerGroupRules } = usePartnerGroupRules(partnerId);
  const { userGroupRules } = useUserGroupRuleValues();

  const { data: smsSettingRows } = usePartnerSettings({
    partnerId,
    partnerSetting: PartnerSetting.SmsSessionsOn,
  });

  const smsGatesPass = useMemo(() => {
    const settingRows = Array.isArray(smsSettingRows) ? smsSettingRows : [];
    const partnerLevel = settingRows.find((row) => row.partnerId !== PLATFORM_PARTNER_ID);
    const platformLevel = settingRows.find((row) => row.partnerId === PLATFORM_PARTNER_ID);
    const smsSettingOn = isStringBooleanTrue((partnerLevel ?? platformLevel)?.value ?? '');

    return (
      smsSettingOn &&
      partnerGroupRules[PartnerGroupRules.SmsSessionsAvailable] === true &&
      userGroupRules[UserGroupRules.AccessToSmsSessions] === true
    );
  }, [smsSettingRows, partnerGroupRules, userGroupRules]);

  const showSmsSwitch = smsGatesPass;

  const { hasOpenSession, sessionId, openedAt } = useOpenSmsSession(
    showSmsSwitch ? conversationId : '',
  );

  const channel = smsGatesPass ? defaultChannel : undefined;
  const channelId = channel?.conversationChannelId ?? null;
  const lineType = channel?.lineType ?? null;
  const lineTypeCheckedAt = channel?.lineTypeCheckedAt ?? null;

  // lazy Twilio Lookup; a failed check leaves no stamp, so a revisit retries
  const checkOnce = useSmsDeliverabilityCheck(conversationId);
  useEffect(() => {
    if (!channelId || lineTypeCheckedAt !== null) return;
    void checkOnce(channelId, { conversationChannelId: channelId });
  }, [channelId, lineTypeCheckedAt, checkOnce]);

  const smsDisabledReason = !isSendableSmsLineType(lineType)
    ? `This number can't receive texts (${lineType})`
    : null;

  return {
    smsGatesPass,
    showSmsSwitch,
    hasOpenSession,
    openSessionId: sessionId,
    sessionOpenedAt: openedAt,
    smsDisabledReason,
  };
}
